import { Injectable } from '@angular/core';
import { Inject } from '@angular/core';
import { ROUTE_NAMES, RouterService } from '@go-modules/services/router/router.service';
import { TaskType } from '@go-modules/interfaces/task';
import { SelectedService, selectedServiceToken } from 'go-modules/services/selected/selected.service';
import { NgxActivityService } from '../activity/activity.service';
import { Observable, of, throwError, from } from 'rxjs';
import { map, switchMap, catchError, tap, take } from 'rxjs/operators';
import { ActivityModelFactory, activityToken } from 'go-modules/models/activity/activity.factory';
import { NgxGroupService } from '../group/group.service';
import { UserService, userServiceToken } from 'go-modules/models/user/user.service';
import { groupToken, Group as GroupModel } from 'go-modules/models/group-dep/group.factory';
import { PubnubService, pubnubToken } from 'go-modules/go-pubnub/pubnub.service';
import { NgxTaskService } from '../task/task.service';
import { sessionToken, Session as SessionModel } from 'go-modules/models/session/session.service';
import { NgxSessionService } from '../session/session.service';
import { FallbackRouteService } from '@dashboard/app/services/fallback-route.service';
import { ActivatedRouteSnapshot, Params } from '@angular/router';
import { FULLSTORY_EVENTS } from 'go-modules/services/fullstory/fullstory.events';
import { NgxFullstoryService } from '../fullstory/fullstory.service';

@Injectable({
	providedIn: 'root'
})
export class RouteResolverService {
	constructor (
		public taskService: NgxTaskService,
		private ngxActivityService: NgxActivityService,
		private ngxGroupService: NgxGroupService,
		private ngxSessionService: NgxSessionService,
		@Inject(selectedServiceToken) private selectedService: SelectedService,
		@Inject(activityToken) private activityFactory: ReturnType<typeof ActivityModelFactory>,
		@Inject(userServiceToken) private userService: UserService,
		@Inject(groupToken) private group: ReturnType<typeof GroupModel>,
		@Inject(pubnubToken) private pubnub: PubnubService,
		@Inject(sessionToken) private sessionFactory: ReturnType<typeof SessionModel>,
		private fullstoryService: NgxFullstoryService,
		private fallbackRouteService: FallbackRouteService,
		private routerService: RouterService
	) {}

	/**
	 * Resolver for task routes - validates access and redirects if necessary
	 */
	public resolveTaskRoute (
		route: ActivatedRouteSnapshot,
		isReport?: boolean
	): Observable<boolean> {
		const activityId = route.parent.params.activity_id;
		const taskType = route.params.type as TaskType;

		return this.ensureActivityLoaded(activityId).pipe(
			switchMap(() => {
				if (!this.taskService.shouldShowTasks()) {
					this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_ACTIVITY_VIEW, {
						activity_id: activityId
					});
					return of(false);
				}

				if (isReport) {
					const group = this.selectedService.getGroup();
					if (!group?.hasReviewerRole(true)) {
						return this.redirectToDefaultTask(activityId).pipe(
							map(() => false)
						);
					}
					// For report routes, we don't need to validate taskType since it's not provided
					return of(true);
				}

				// Only validate taskType for non-report routes
				if (!this.taskService.isValidTaskType(taskType)) {
					return this.redirectToDefaultTask(activityId).pipe(
						map(() => false)
					);
				}

				return this.taskService.canAccessTaskType(taskType).pipe(
					switchMap((canAccessTaskType) => {
						if (!canAccessTaskType) {
							return this.redirectToDefaultTask(activityId).pipe(
								map(() => false)
							);
						}
						return of(true);
					})
				);
			}),
			catchError(() => {
				this.fallbackRouteService.navigate();
				return of(false);
			})
		);
	}

	public resolveFoldersRoute (stateParams: Params): Observable<boolean> {
		const folderId = stateParams.id;

		if (!folderId) {
			this.fallbackRouteService.navigate();
			return of(false);
		}

		return this.ensureOrgLoaded(parseInt(folderId, 10)).pipe(
			map(() => true),
			catchError(() => {
				this.fallbackRouteService.navigate();
				return of(false);
			})
		);
	}

	/**
	 * Resolver for folder view route - ensures group is loaded
	 */
	public resolveFolderRoute (stateParams: Params): Observable<boolean> {
		const folderId = stateParams.folder_id;

		if (!folderId) {
			this.fallbackRouteService.navigate();
			return of(false);
		}

		return this.ensureGroupLoaded(parseInt(folderId, 10)).pipe(
			map(() => true),
			catchError(() => {
				this.fallbackRouteService.navigate();
				return of(false);
			})
		);
	}

	/**
	 * Resolver for base activity route - redirects to default task if tasks are enabled
	 */
	public resolveActivityRoute (targetState: ROUTE_NAMES, stateParams: Params): Observable<boolean> {
		const activityId = stateParams.activity_id;

		return this.ensureActivityLoaded(activityId).pipe(
			switchMap(() => {
				// Only redirect to default task if we're on the base activity view
				if (targetState === ROUTE_NAMES.DASHBOARD_ACTIVITY_VIEW && this.taskService.shouldShowTasks()) {
					return this.taskService.getDefaultTaskTypeForUser().pipe(
						tap((defaultTaskType) => {
							this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_ACTIVITY_TASK, {
								activity_id: activityId, type: defaultTaskType
							});
						}),
						map(() => false)
					);
				}

				return of(true);
			}),
			catchError(() => {
				this.fallbackRouteService.navigate();
				return of(false);
			})
		);
	}

	public resolveSessionRoute (stateParams: Params): Observable<boolean> {
		const sessionId = stateParams.session_id;
		return this.ensureSessionLoaded(sessionId).pipe(
			switchMap(() => {
				if (!this.taskService.shouldShowTasks()) {
					return of(true);
				}

				const activityId = this.selectedService.getActivity().activity_id;
				return this.taskService.getTasks(activityId).pipe(
					take(1),
					map(() => true)
				);
			}),
			catchError(() => {
				this.fallbackRouteService.navigate();
				return of(false);
			})
		);
	}

	public resolveLicenseManagementRoute (params: Params): Observable<boolean> {
		// If they have an org selected, then we don't need to do anything special
		if (params.orgId != null) {
			return of(true);
		}

		// Otherwise, without a specified orgId, we'll need to pick an org for them.
		// Try to use the currently selected org, or fall back to account org or first available group
		let orgId = null;
		if (this.selectedService.getOrg()) {
			orgId = this.selectedService.getOrg().group_id;
		} else if (this.selectedService.getAccount()) {
			orgId = parseInt(this.selectedService.getAccount().org_id, 10);
		} else if (this.selectedService.getGroups().length > 0) {
			orgId = parseInt(this.selectedService.getGroups()[0].org_id, 10);
		}

		if (orgId == null) {
			this.fallbackRouteService.navigate();
			return of(false);
		}

		return this.ensureOrgLoaded(orgId).pipe(
			tap(() => {
				if (this.userService.currentUser.is_root_user) {
					this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_LICENSE_MANAGEMENT_ORG, { orgId });
				}
			}),
			map(() => false),
			catchError(() => {
				this.fallbackRouteService.navigate();
				return of(false);
			})
		);
	}

	/**
	 * Redirect to the default task type for the current user
	 */
	private redirectToDefaultTask (activityId: string): Observable<void> {
		return this.taskService.getDefaultTaskTypeForUser().pipe(
			tap((defaultTaskType) => {
				this.routerService.navigateTo(ROUTE_NAMES.DASHBOARD_ACTIVITY_TASK, {
					activity_id: activityId, type: defaultTaskType
				});
			}),
			map(() => void 0)
		);
	}

	public ensureActivityLoaded (activityId: string): Observable<void> {
		const currentActivity = this.selectedService.getActivity();
		if (currentActivity && currentActivity.activity_id === parseInt(activityId, 10)) {
			return this.ensureGroupLoaded(currentActivity.group_id, false);
		}

		return this.ngxActivityService.get(parseInt(activityId, 10)).pipe(
			switchMap((activity) => {
				if (!('unviewed_comments' in activity)) {
					return this.ngxActivityService.getStats(activity.group_id).pipe(
						map(() => activity)
					);
				}
				return of(activity);
			}),
			tap((activity) => {
				this.selectedService.setActivity(this.activityFactory.model(activity));
			}),
			switchMap((activity) => this.ensureGroupLoaded(activity.group_id, false)),
			catchError((error) => {
				if (error instanceof Error && error.message.includes('not available for route resolution')) {
					return throwError(() => error);
				}
				return throwError(() => new Error(`Activity ${activityId} not available for route resolution`));
			})
		);
	}

	public ensureGroupLoaded (groupId: number, shouldUpdateTitle: boolean = true): Observable<void> {
		const currentGroup = this.selectedService.getGroup();
		if (currentGroup && currentGroup.group_id === groupId) {
			return this.ensureOrgLoaded(currentGroup.org_id);
		}

		return this.ngxGroupService.getCourse(groupId).pipe(
			tap((group) => {
				this.selectedService.setGroup(this.group.model(group), undefined, shouldUpdateTitle);
				this.groupIsLoaded(group);
			}),
			switchMap((group) => this.ensureOrgLoaded(group.org_id)),
			catchError((error) => {
				if (error instanceof Error && error.message.includes('not available for route resolution')) {
					return throwError(() => error);
				}
				return throwError(() => new Error(`Group ${groupId} not available for route resolution`));
			})
		);
	}

	public ensureOrgLoaded (orgId: number): Observable<void> {
		const currentOrg = this.selectedService.getOrg();
		if (currentOrg && currentOrg.group_id === orgId) {
			return of(void 0);
		}

		const myOrg = this.selectedService.getMyOrgs().find((org) => {
			return parseInt(org.group_id, 10) === orgId;
		});

		if (myOrg) {
			return from(this.selectedService.setOrg(this.group.model(myOrg))).pipe(
				map(() => void 0)
			);
		}

		if (this.userService.currentUser.is_root_user) {
			return this.ngxGroupService.getOrg(orgId).pipe(
				switchMap((org) => {
					return from(this.selectedService.setOrg(this.group.model(org))).pipe(
						map(() => void 0)
					);
				}),
				catchError(() => {
					return throwError(() => new Error(`Org ${orgId} not available for route resolution`));
				})
			);
		}

		return throwError(() => new Error(`Org ${orgId} not available for route resolution`));
	}

	public ensureSessionLoaded (sessionId: string): Observable<void> {
		const currentSession = this.selectedService.getSession();
		if (currentSession && currentSession.session_id === parseInt(sessionId, 10)) {
			return this.ensureActivityLoaded(currentSession.activity_id);
		}

		return this.ngxSessionService.getSession(parseInt(sessionId, 10), [
			'activity',
			'activity.attachments',
			'activity.llmDefinitions',
			'activity.recording_instructions',
			'activity.feedback_instructions',
			'player_time_sync_events',
			'activity.source_media'
		]).pipe(
			tap((session) => {
				session.is_fully_loaded = true;
				this.selectedService.setSession(this.sessionFactory.model(session));
				this.selectedService.setActivity(this.activityFactory.model(session.activity));
			}),
			switchMap((session) => this.ensureGroupLoaded(session.group_id, false)),
			catchError((error) => {
				if (error instanceof Error && error.message.includes('not available for route resolution')) {
					return throwError(() => error);
				}
				return throwError(() => new Error(`Session ${sessionId} not available for route resolution`));
			})
		);
	}

	private groupIsLoaded (group): void {
		this.fullstoryService.createEvent(FULLSTORY_EVENTS.USER_CONTEXT_IDENTIFIED, {
			userRole_str: group.role,
			groupId_int: parseInt(group.group_id, 10),
			orgId_int: parseInt(group.org_id, 10)
		});

		if (this.selectedService.getAccount()) {
			this.fullstoryService.createEvent(FULLSTORY_EVENTS.USE_TYPE_IDENTIFIED, {
				useTypeId_int: parseInt(this.selectedService.getAccount().use_type_id, 10)
			});
		}

		this.pubnub.updateChannels();
	}
}
